/**
 * One rate, rendered so that free, unknown, included and unavailable are all
 * distinguishable without seeing colour: each carries a glyph and a word.
 */

import type { Rate, RateSet } from "@pricing/contracts";

import { describeRate } from "../lib/format.ts";

export function RateCell({ rate }: { rate: Rate }): React.JSX.Element {
  const display = describeRate(rate);
  return (
    <span className={`rate rate--${display.kind}`} title={display.title}>
      {display.label}
    </span>
  );
}

const CACHE_WRITES = [
  { bucket: "cache_write", label: null },
  { bucket: "cache_write_5m", label: "5m" },
  { bucket: "cache_write_1h", label: "1h" },
] as const;

/**
 * Every cache-write rate a seller bills, in one cell. Sellers split cache
 * writes differently — OpenAI bills one unsplit rate, Anthropic one per cache
 * lifetime — so separate columns left each seller's real rate in a column the
 * other one's rows filled with "not billed", and OpenAI's in no column at all.
 */
export function CacheWriteCell({ rates }: { rates: RateSet }): React.JSX.Element {
  const billed = CACHE_WRITES.filter(({ bucket }) => rates[bucket].state !== "not_billed");
  // Nothing billed, or nothing known about any of it: one word says it.
  if (billed.length === 0) return <RateCell rate={rates.cache_write} />;
  if (billed.every(({ bucket }) => rates[bucket].state === "unknown")) {
    return <RateCell rate={rates[billed[0]!.bucket]} />;
  }
  if (billed.length === 1) return <RateCell rate={rates[billed[0]!.bucket]} />;
  return (
    <span className="rate-stack">
      {billed.map(({ bucket, label }) => (
        <span key={bucket}>
          {label ? <span className="rate-stack__label">{label} </span> : null}
          <RateCell rate={rates[bucket]} />
        </span>
      ))}
    </span>
  );
}
